import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";
import { Loader2, TrendingUp, DollarSign, RefreshCw, AlertCircle, Apple } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/features/auth/model/AuthContext";
import { UserRole } from "@/shared/types/roles";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/ui/tabs";
import { Skeleton } from "@/shared/ui/skeleton";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { DateRangePicker } from "@/shared/widgets/DateRangePicker";
import { useDefaultDatePresets } from "@/shared/widgets/DateRangePicker/presets";

type Platform = "ios" | "android" | "all";

type DailyRow = {
  date: string;
  units: number;
  newSubs: number;
  renewals: number;
  refunds: number;
  cancels: number;
  proceeds: number;
};
type MonthlyRow = { month: string; proceeds: number };
type SummaryResponse = {
  range: { start: string; end: string };
  platform: Platform;
  totals: {
    units: number; newSubs: number; renewals: number; refunds: number; cancels: number; proceeds: number;
  };
  daily: DailyRow[];
  monthly: MonthlyRow[];
  projection: {
    month: string; elapsedDays: number; totalDays: number;
    accumulated: number; dailyAverage: number; projected: number;
  };
};

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
const fmtNumber = (n: number) => new Intl.NumberFormat("pt-BR").format(n || 0);
const fmtDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function AppFinancialPage() {
  const { user } = useAuth();
  if (user?.role !== UserRole.ADMIN) return <Navigate to="/unauthorized" replace />;

  const presets = useDefaultDatePresets();
  const [platform, setPlatform] = useState<Platform>("ios");
  const [range, setRange] = useState<DateRange | undefined>(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(start.getDate() - 29);
    return { from: start, to: end };
  });

  const queryParams = useMemo(() => ({
    start: range?.from ? fmtDate(range.from) : undefined,
    end: range?.to ? fmtDate(range.to) : undefined,
    platform,
  }), [range, platform]);

  const { data, isLoading, isFetching, error, refetch } = useQuery<SummaryResponse>({
    queryKey: ["apple-financial-summary", queryParams],
    queryFn: async () => {
      const usp = new URLSearchParams();
      if (queryParams.start) usp.set("start", queryParams.start);
      if (queryParams.end) usp.set("end", queryParams.end);
      usp.set("platform", queryParams.platform);
      usp.set("action", "summary");
      const token = import.meta.env.VITE_APPLE_REPORTS_TOKEN as string | undefined;
      if (!token) throw new Error("VITE_APPLE_REPORTS_TOKEN não configurado no .env");
      const { data, error } = await supabase.functions.invoke(`apple-financial-reports?${usp.toString()}`, {
        method: "GET",
        headers: { "x-agent-token": token },
      });
      if (error) throw error;
      if ((data as { error?: string })?.error) throw new Error((data as { message?: string }).message || (data as { error: string }).error);
      return data as SummaryResponse;
    },
    enabled: !!queryParams.start && !!queryParams.end && platform !== "android",
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const errorMessage = error instanceof Error ? error.message : null;

  return (
    <PageContent
      title="Faturamento App"
      items={[{ label: "Dashboard", href: "/" }, { label: "Faturamento App", href: "/app-financial" }]}
    >
      <div className="min-h-screen bg-[#1A1A1A] text-white">
        <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-amber-500 font-semibold mb-1">Fonte: App Store Connect</h3>
            <p className="text-sm text-white/70">
              Dados consumidos diretamente da API oficial da Apple. Vendas têm defasagem de ~24h.
              Relatórios financeiros (proceeds líquidos) ficam disponíveis ~5 dias após o fechamento do mês fiscal.
              Integração com Google Play virá em uma próxima fase.
            </p>
          </div>
        </div>

        <div className="sticky top-16 z-20 bg-[#1A1A1A] py-2 mb-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[180px]">
              <label className="text-sm font-medium text-white/60 mb-2 block">Plataforma</label>
              <Select value={platform} onValueChange={(v) => setPlatform(v as Platform)}>
                <SelectTrigger className="bg-[#232323] border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#232323] border-white/10">
                  <SelectItem value="ios">
                    <span className="flex items-center gap-2"><Apple className="w-4 h-4" /> iOS</span>
                  </SelectItem>
                  <SelectItem value="android" disabled>Android (em breve)</SelectItem>
                  <SelectItem value="all" disabled>Todos (em breve)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[280px]">
              <label className="text-sm font-medium text-white/60 mb-2 block">Período</label>
              <DateRangePicker dateRange={range} setDateRange={setRange} presets={presets} />
            </div>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="bg-[#232323] border-white/10 text-white hover:bg-white/10"
              disabled={isFetching}
            >
              {isFetching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Atualizar
            </Button>
          </div>
        </div>

        {errorMessage && (
          <Card className="mb-6 bg-red-500/10 border-red-500/30">
            <CardContent className="pt-6 text-red-300 text-sm">{errorMessage}</CardContent>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard title="Proceeds (período)" value={isLoading ? null : fmtCurrency(data?.totals.proceeds ?? 0)} icon={DollarSign} />
          <KpiCard title="Vendas + Renovações" value={isLoading ? null : fmtNumber(data?.totals.units ?? 0)} />
          <KpiCard title="Novas assinaturas" value={isLoading ? null : fmtNumber(data?.totals.newSubs ?? 0)} />
          <KpiCard title="Cancelamentos" value={isLoading ? null : fmtNumber(data?.totals.cancels ?? 0)} sub={`${fmtNumber(data?.totals.refunds ?? 0)} refunds`} />
        </div>

        {/* Projeção */}
        <Card className="mb-6 bg-[#232323] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Projeção de faturamento — {data?.projection.month ?? "—"}
            </CardTitle>
            <CardDescription className="text-white/60">
              Baseada no pace diário do mês corrente (média × dias totais).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-24 bg-white/5" />
            ) : data ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Acumulado" value={fmtCurrency(data.projection.accumulated)} />
                <Stat label="Média diária" value={fmtCurrency(data.projection.dailyAverage)} />
                <Stat label={`Dias decorridos`} value={`${data.projection.elapsedDays}/${data.projection.totalDays}`} />
                <Stat label="Projetado" value={fmtCurrency(data.projection.projected)} highlight />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Tabs defaultValue="daily" className="w-full">
          <TabsList className="bg-[#232323] border border-white/10">
            <TabsTrigger value="daily">Diário</TabsTrigger>
            <TabsTrigger value="subs">Assinaturas</TabsTrigger>
            <TabsTrigger value="monthly">Proceeds Mensais</TabsTrigger>
          </TabsList>

          <TabsContent value="daily">
            <ChartCard title="Vendas, renovações e proceeds (diário)">
              {isLoading ? <Skeleton className="h-72 bg-white/5" /> : (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={data?.daily ?? []}>
                    <CartesianGrid stroke="#ffffff10" />
                    <XAxis dataKey="date" stroke="#ffffff60" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" stroke="#ffffff60" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" stroke="#ffffff60" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#232323", border: "1px solid #ffffff20" }} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="units" name="Unidades" stroke="#60a5fa" dot={false} />
                    <Line yAxisId="left" type="monotone" dataKey="renewals" name="Renovações" stroke="#34d399" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="proceeds" name="Proceeds (USD)" stroke="#fbbf24" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </TabsContent>

          <TabsContent value="subs">
            <ChartCard title="Eventos de assinatura (diário)">
              {isLoading ? <Skeleton className="h-72 bg-white/5" /> : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={data?.daily ?? []}>
                    <CartesianGrid stroke="#ffffff10" />
                    <XAxis dataKey="date" stroke="#ffffff60" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#ffffff60" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#232323", border: "1px solid #ffffff20" }} />
                    <Legend />
                    <Bar dataKey="newSubs" name="Novas" fill="#34d399" />
                    <Bar dataKey="renewals" name="Renovações" fill="#60a5fa" />
                    <Bar dataKey="cancels" name="Cancelamentos" fill="#f87171" />
                    <Bar dataKey="refunds" name="Refunds" fill="#fb923c" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </TabsContent>

          <TabsContent value="monthly">
            <ChartCard title="Proceeds líquidos mensais (Financial Reports)">
              {isLoading ? <Skeleton className="h-72 bg-white/5" /> : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={data?.monthly ?? []}>
                    <CartesianGrid stroke="#ffffff10" />
                    <XAxis dataKey="month" stroke="#ffffff60" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#ffffff60" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCurrency(v)} />
                    <Tooltip
                      contentStyle={{ background: "#232323", border: "1px solid #ffffff20" }}
                      formatter={(v: number) => fmtCurrency(v)}
                    />
                    <ReferenceLine y={0} stroke="#ffffff40" />
                    <Bar dataKey="proceeds" name="Proceeds líquido" fill="#fbbf24" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </TabsContent>
        </Tabs>
      </div>
    </PageContent>
  );
}

function KpiCard({ title, value, sub, icon: Icon }: { title: string; value: string | null; sub?: string; icon?: React.ComponentType<{ className?: string }> }) {
  return (
    <Card className="bg-[#232323] border-white/10">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-white/60">{title}</span>
          {Icon ? <Icon className="w-4 h-4 text-white/40" /> : null}
        </div>
        {value === null ? <Skeleton className="h-7 w-24 bg-white/5" /> : <div className="text-2xl font-semibold text-white">{value}</div>}
        {sub ? <div className="text-xs text-white/50 mt-1">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className="text-xs text-white/50 mb-1">{label}</div>
      <div className={`text-xl font-semibold ${highlight ? "text-emerald-400" : "text-white"}`}>{value}</div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="bg-[#232323] border-white/10 mt-4">
      <CardHeader><CardTitle className="text-white text-base">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
